/**
 * Resolves catalog-page canonical URL and robots indexability.
 */
import {
  CANONICAL_MAP,
  FORCE_INDEX_PATHS,
  FORCE_NOINDEX_PATHS,
  P2_AUTO_CANONICAL_EXCLUDED_PATHS,
} from "./seo-policy";
import { spacesToDashes } from "../helpers";

/**
 * @typedef {Object} CatalogSeoInput
 * @property {Object} route Nuxt route object from the catalog page.
 * @property {string} route.path Route path without query.
 * @property {string} route.fullPath Route path with query.
 * @property {Object} runtimeConfig Nuxt runtime config from the catalog page.
 * @property {string} runtimeConfig.baseUrlApp Public site origin.
 * @property {string} title Final page title used in the head tag.
 * @property {number} profilesCount Number of profiles shown on the catalog page.
 * @property {string} city Current decoded city title.
 * @property {Object|null|undefined} decodedDistrict Current decoded district object, when route includes district.
 * @property {Array<Object>|null|undefined} decodedRouteParams Backend-decoded catalog route params.
 */

/**
 * @typedef {Object} CatalogSeoContext
 * @property {string} path Route path without query, for exact path rules.
 * @property {string} fullPath Route path with query, used by the legacy self-canonical fallback.
 * @property {string} baseUrlApp Public site origin from runtime config.
 * @property {string} nodeEnv Current NODE_ENV value.
 * @property {string} title Final page title used in the head tag.
 * @property {boolean} isRouteIncludeGender Whether the current route path contains a supported gender segment.
 * @property {number} profilesCount Number of profiles shown on the catalog page.
 * @property {string} city Current decoded city title.
 * @property {Object|null|undefined} decodedDistrict Current decoded district object, when route includes district.
 * @property {Array<Object>|null|undefined} decodedRouteParams Backend-decoded catalog route params.
 */

/**
 * @typedef {Object} CatalogSeoState
 * @property {CatalogSeoContext} context Input data used by catalog SEO rules.
 * @property {string} canonicalUrl Final canonical URL.
 * @property {boolean} isIndexNeeded True means robots content should be "all"; false means "noindex".
 * @property {string} robotsContent Final robots meta content.
 * @property {boolean} isIndexDecisionLocked Prevents later index rules from overriding a hard noindex decision.
 * @property {string[]} canonicalRules Trace of canonical rules that matched.
 * @property {string[]} indexRules Trace of index rules that matched.
 */

/**
 * @callback CatalogSeoRule
 * @param {CatalogSeoState} state
 * @return {CatalogSeoState}
 */

const CATALOG_SEO_RULES = Object.freeze({
  DEFAULT_SELF_CANONICAL: "default:self-canonical",
  DEFAULT_NOINDEX: "default:noindex",
  CANONICAL_MANUAL_MAP: "canonical:manual-map",
  CANONICAL_P2_DISTRICT_ENTITY: "canonical:p2-district-entity",
  INDEX_HARD_NOINDEX: "index:hard-noindex",
  INDEX_MANUAL_NOINDEX: "index:manual-noindex",
  INDEX_BASE_CATALOG: "index:base-catalog-rule",
  INDEX_MANUAL_ALLOWLIST: "index:manual-allowlist",
});

const CATALOG_GENDERS = ["male", "female", "transgender"];
const MIN_INDEXABLE_PROFILES_COUNT = 5;
const P2_AUTO_CANONICAL_TITLE_PATTERN =
  /^Enjoy Sensual .+ Services \| Emiratescort$/;

/**
 * Resolves all SEO decisions for a catalog page.
 *
 * @export
 * @param {CatalogSeoInput} input
 * @return {CatalogSeoState}
 *
 * @example
 * resolveCatalogSeo({
 *   route: { path: "/dubai/female", fullPath: "/dubai/female" },
 *   runtimeConfig: { baseUrlApp: "https://emiratescort.com" },
 *   title: "Dubai Female Escorts | Emiratescort",
 *   profilesCount: 12,
 *   city: "Dubai",
 *   decodedDistrict: null,
 *   decodedRouteParams: [{ type: "location" }, { type: "gender" }],
 * })
 */
export function resolveCatalogSeo(input) {
  const context = normalizeCatalogSeoInput(input);
  const initialState = createInitialState(context);
  const canonicalState = resolveCanonicalSeo(initialState);
  const indexState = resolveIndexSeo(initialState);

  return {
    ...initialState,
    canonicalUrl: canonicalState.canonicalUrl,
    isIndexNeeded: indexState.isIndexNeeded,
    robotsContent: indexState.robotsContent,
    isIndexDecisionLocked: indexState.isIndexDecisionLocked,
    canonicalRules: canonicalState.canonicalRules,
    indexRules: indexState.indexRules,
  };
}

/**
 * Converts page-level Nuxt data into the internal rule context.
 *
 * @param {CatalogSeoInput} input
 * @return {CatalogSeoContext}
 */
function normalizeCatalogSeoInput(input = {}) {
  const path = input.route?.path || "";

  return {
    path,
    fullPath: input.route?.fullPath || path,
    baseUrlApp: input.runtimeConfig?.baseUrlApp || "",
    nodeEnv: process.env.NODE_ENV,
    title: input.title || "",
    isRouteIncludeGender: CATALOG_GENDERS.some((gender) =>
      path.includes(gender)
    ),
    profilesCount: input.profilesCount,
    city: input.city,
    decodedDistrict: input.decodedDistrict,
    decodedRouteParams: input.decodedRouteParams,
  };
}

/**
 * Creates the default catalog SEO state before any rule is applied.
 *
 * Defaults intentionally match the old component behavior:
 * - canonical points to the current full path
 * - page is noindex until an index rule explicitly allows indexing
 *
 * @param {CatalogSeoContext} context
 * @return {CatalogSeoState}
 */
function createInitialState(context) {
  return {
    context,
    canonicalUrl: `${context.baseUrlApp}${context.fullPath}`,
    isIndexNeeded: false,
    robotsContent: "noindex",
    isIndexDecisionLocked: false,
    canonicalRules: [CATALOG_SEO_RULES.DEFAULT_SELF_CANONICAL],
    indexRules: [CATALOG_SEO_RULES.DEFAULT_NOINDEX],
  };
}

// -------------------------------------------------------------------

/**
 * Applies canonical-only rules.
 *
 * Precedence:
 * 1. P-2 auto canonical skips GSC whitelist paths.
 * 2. P-2 auto canonical handles other eligible district+entity pages.
 * 3. The historical exact map runs last and preserves pre-P-2 behavior.
 *
 * @param {CatalogSeoState} state
 * @return {CatalogSeoState}
 */
function resolveCanonicalSeo(state) {
  return [applyP2DistrictEntityCanonicalRule, applyCanonicalMapRule].reduce(
    (currentState, rule) => rule(currentState),
    state
  );
}

/**
 * Applies PDF P-2 automatic canonical for non-whitelisted district+entity pages.
 *
 * District-only pages are excluded structurally because they do not have a
 * third taxonomy/entity route param.
 *
 * @type {CatalogSeoRule}
 */
function applyP2DistrictEntityCanonicalRule(state) {
  const canonicalPath = resolveP2DistrictEntityCanonicalPath(state.context);
  if (!canonicalPath) return state;

  return {
    ...state,
    canonicalUrl: `${state.context.baseUrlApp}${canonicalPath}`,
    canonicalRules: [
      ...state.canonicalRules,
      CATALOG_SEO_RULES.CANONICAL_P2_DISTRICT_ENTITY,
    ],
  };
}

/**
 * Builds a city-level canonical path for district+entity routes from PDF P-2.
 *
 * @param {CatalogSeoContext} context
 * @return {string}
 */
function resolveP2DistrictEntityCanonicalPath(context) {
  if (P2_AUTO_CANONICAL_EXCLUDED_PATHS.includes(context.path)) return "";
  if (!isP2AutoCanonicalTitle(context.title)) return "";
  if (!context.decodedDistrict) return "";
  if (!isDistrictGenderEntityRoute(context.decodedRouteParams)) return "";

  const pathSegments = getPathSegments(context.path);
  if (pathSegments.length < 3) return "";

  const [locationSegment, ...entitySegments] = pathSegments;
  const citySlug = resolveCitySlugFromDistrictSegment(
    locationSegment,
    context.decodedDistrict
  );
  if (!citySlug) return "";

  return `/${[citySlug, ...entitySegments].join("/")}`;
}

/**
 * Checks the PDF P-2 title marker for autogenerated thin district+entity pages.
 *
 * @param {string} title
 * @return {boolean}
 */
function isP2AutoCanonicalTitle(title = "") {
  return P2_AUTO_CANONICAL_TITLE_PATTERN.test(title);
}

/**
 * Checks route meaning from backend-decoded params, not only from URL shape.
 *
 * @param {Array<Object>|null|undefined} decodedRouteParams
 * @return {boolean}
 */
function isDistrictGenderEntityRoute(decodedRouteParams) {
  if (!Array.isArray(decodedRouteParams)) return false;

  const hasGender = decodedRouteParams.some((param) => param.type == "gender");
  const hasEntity = decodedRouteParams.some(
    (param) => param.type && !["location", "gender"].includes(param.type)
  );

  return hasGender && hasEntity;
}

/**
 * Resolves city slug from a district route segment.
 *
 * Example: `dubai-business-bay` + `Business Bay` -> `dubai`.
 * This avoids depending on `getCurrentCityUrl`, which is also derived data.
 *
 * @param {string} locationSegment
 * @param {Object} decodedDistrict
 * @return {string}
 */
function resolveCitySlugFromDistrictSegment(locationSegment, decodedDistrict) {
  const districtSlug = spacesToDashes(decodedDistrict?.title);
  if (!locationSegment || !districtSlug) return "";

  const districtSuffix = `-${districtSlug}`;
  if (!locationSegment.endsWith(districtSuffix)) return "";

  return locationSegment.slice(0, -districtSuffix.length);
}

/**
 * Splits a route path into non-empty URL segments.
 *
 * @param {string} path
 * @return {string[]}
 */
function getPathSegments(path = "") {
  return path.split("/").filter(Boolean);
}

/**
 * Applies exact canonical overrides from the historical canonical map.
 *
 * @type {CatalogSeoRule}
 */
function applyCanonicalMapRule(state) {
  const canonicalPath = CANONICAL_MAP[state.context.path];
  if (!canonicalPath) return state;

  return {
    ...state,
    canonicalUrl: `${state.context.baseUrlApp}${canonicalPath}`,
    canonicalRules: [
      ...state.canonicalRules,
      CATALOG_SEO_RULES.CANONICAL_MANUAL_MAP,
    ],
  };
}

// -------------------------------------------------------------------

/**
 * Applies index-only rules.
 *
 * Rule order matters:
 * 1. hard noindex may lock the index decision;
 * 2. manual noindex may lock the index decision;
 * 3. base catalog rule may allow indexing;
 * 4. manual allowlist may allow indexing for historical exact-path exceptions.
 *
 * @param {CatalogSeoState} state
 * @return {CatalogSeoState}
 */
function resolveIndexSeo(state) {
  return [
    applyHardNoindexRule,
    applyManualNoindexRule,
    applyBaseIndexRule,
    applyManualIndexAllowlistRule,
  ].reduce((currentState, rule) => rule(currentState), state);
}

/**
 * Preserves the old hard noindex behavior for a known unwanted catalog URL.
 *
 * @type {CatalogSeoRule}
 */
function applyHardNoindexRule(state) {
  if (!state.context.path.includes("/dubai/male/for-man-services-escorts")) {
    return state;
  }

  return {
    ...state,
    isIndexNeeded: false,
    robotsContent: "noindex",
    isIndexDecisionLocked: true,
    indexRules: [...state.indexRules, CATALOG_SEO_RULES.INDEX_HARD_NOINDEX],
  };
}

/**
 * Applies exact `noindex,follow` rules from the second SEO specification.
 *
 * @type {CatalogSeoRule}
 */
function applyManualNoindexRule(state) {
  if (!FORCE_NOINDEX_PATHS.includes(state.context.path)) return state;

  return {
    ...state,
    isIndexNeeded: false,
    robotsContent: "noindex,follow",
    isIndexDecisionLocked: true,
    indexRules: [...state.indexRules, CATALOG_SEO_RULES.INDEX_MANUAL_NOINDEX],
  };
}

/**
 * Applies the generic catalog index rule from the legacy component.
 *
 * @type {CatalogSeoRule}
 */
function applyBaseIndexRule(state) {
  if (state.isIndexDecisionLocked) return state;

  const {
    nodeEnv,
    isRouteIncludeGender,
    profilesCount,
    city,
    decodedDistrict,
    decodedRouteParams,
  } = state.context;

  const isDeepDistrictRoute = Boolean(
    city && decodedDistrict && decodedRouteParams?.length >= 3
  );
  const isBaseIndexAllowed =
    nodeEnv == "production" &&
    isRouteIncludeGender &&
    profilesCount >= MIN_INDEXABLE_PROFILES_COUNT &&
    !isDeepDistrictRoute;

  if (!isBaseIndexAllowed) return state;

  return {
    ...state,
    isIndexNeeded: true,
    robotsContent: "all",
    indexRules: [...state.indexRules, CATALOG_SEO_RULES.INDEX_BASE_CATALOG],
  };
}

/**
 * Allows indexing for historical exact-path exceptions.
 *
 * @type {CatalogSeoRule}
 */
function applyManualIndexAllowlistRule(state) {
  if (state.isIndexDecisionLocked) return state;

  const isManualIndexAllowed =
    state.context.nodeEnv == "production" &&
    FORCE_INDEX_PATHS.includes(state.context.path);

  if (!isManualIndexAllowed) return state;

  return {
    ...state,
    isIndexNeeded: true,
    robotsContent: "all",
    indexRules: [...state.indexRules, CATALOG_SEO_RULES.INDEX_MANUAL_ALLOWLIST],
  };
}
